import { AI_SUPPORTED_ASSET_TYPES, ASSET_TYPES, SUPPORTED_ASSET_TYPES } from 'personalization-lib/constants/assetTypes';
import { ASSET_PERSONALIZATION_SCOPES, READ_SCOPES, WRITE_SCOPES } from 'personalization-lib/constants/auth';
import { hasScope, hasScopes } from 'personalization-lib/utils/auth/userPermissions';
import { getFeatureGates } from 'personalization-lib/utils/auth/featureAccess';

/**
 * Returns true if the user has write access to the asset type.
 *
 * @param assetType - The asset type to check.
 * @returns Returns true/false if the user has write access to the asset type.
 */
export const canEditAssetType = assetType => Boolean(WRITE_SCOPES[assetType] && hasScopes(WRITE_SCOPES[assetType]));

/**
 * Returns true if the user has asset-specific personalization scope.
 * If the asset type has no scope mapping, returns true.
 */
export const hasAssetPersonalizationScope = assetType => {
  var _ASSET_PERSONALIZATIO;
  return hasScopes((_ASSET_PERSONALIZATIO = ASSET_PERSONALIZATION_SCOPES[assetType]) !== null && _ASSET_PERSONALIZATIO !== void 0 ? _ASSET_PERSONALIZATIO : []);
};

/**
 * Returns true if the user has read access to the asset type.
 *
 * @param assetType - The asset type to check.
 * @returns Returns true/false if the user has read access to the asset type.
 */
export const canReadAssetType = assetType => {
  const hasRequiredScope = Boolean(READ_SCOPES[assetType] && hasScope(READ_SCOPES[assetType]));
  if (assetType === ASSET_TYPES.WEBSITE_PAGE) {
    return hasRequiredScope && getFeatureGates().websitePages;
  }
  return hasRequiredScope;
};

/**
 * Returns the asset types that the user has write access to.
 *
 * @param assetTypes - The asset types to filter. Defaults to all supported asset types.
 * @returns The asset types that the user has write access to.
 */
export const getWritableAssetTypes = (assetTypes = [...SUPPORTED_ASSET_TYPES]) => assetTypes.filter(canEditAssetType);

/**
 * Returns the asset types that the user has read access to.
 *
 * @param assetTypes - The asset types to filter. Defaults to all supported asset types.
 * @returns The asset types that the user has read access to.
 */
export const getReadableAssetTypes = (assetTypes = [...SUPPORTED_ASSET_TYPES]) => assetTypes.filter(canReadAssetType);

/**
 * Returns true if AI variant creation is supported for the asset type.
 *
 * @param assetType - The asset type to check. If undefined, returns true.
 * @returns Returns true if AI is supported for the asset type or if no asset type is provided.
 */
export const canCreateAiVariantForAssetType = assetType => !assetType || AI_SUPPORTED_ASSET_TYPES.includes(assetType);